import { type FC, type RefObject, useMemo, useRef, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import type Handsontable from 'handsontable-v16';
import type { CellValue } from '@/support/hot-v16.types';
import ReactModal from 'react-modal';

// v16 imports
import { HotTable, type HotTableRef } from './HotTableV16';
import {
  registerHOTModulesV16,
  rowHeaderWithControlsRendererV16,
  defaultMetadataToColumnSettingMapperV16,
} from '@/support/hot-v16';

import type {
  SpreadSheetErrorV16,
  SpreadsheetV16Settings,
  CustomValidationV16,
  CustomColumnSettingsV16,
  ColumnSettings,
} from './SpreadsheetV16.model';
export type { SpreadsheetV16Settings };
import type { MetadataField } from '@/model/Metadata.model';
import type { ID } from '@/model/Common.model';

import Button from '@/components/UI/Button';
import AddMetaColumn from '@/components/Modals/AddMetaColumn/AddMetaColumn';
import { buildCustomValidatorsV16, createValidationErrorV16 } from './SpreadsheetV16.utils';
import { _isNotEmpty, _notNil, _isEmpty } from '@/littledash';

export type CompatibleCellChange = [number, string | number, unknown, unknown];
export type CompatibleChangeSource = string;

type CompatibleHotInstance = Handsontable.Core;

const isCompatibleHotInstance = (hot: unknown): hot is Handsontable.Core => {
  return (
    _notNil(hot) &&
    typeof (hot as Handsontable.Core).validateRows === 'function' &&
    typeof (hot as Handsontable.Core).validateCells === 'function'
  );
};

registerHOTModulesV16();

const clearCellTooltip = (cell: HTMLTableCellElement | null | undefined): void => {
  cell?.removeAttribute('data-tooltip-content');
  cell?.removeAttribute('data-tooltip-id');
  cell?.removeAttribute('data-type');
};

export interface SpreadsheetV16Props {
  data: Array<Record<string, unknown>> | Array<Array<unknown>>;
  settings: SpreadsheetV16Settings;
  setSettings?: (settings: SpreadsheetV16Settings) => void;
  innerRef: RefObject<HotTableRef>;
  className?: string;
  afterChange?: (changes: Array<CompatibleCellChange> | null, source: CompatibleChangeSource) => void;
  afterCreateRow?: (index: number, amount: number, source?: CompatibleChangeSource) => void;
  afterRemoveRow?: (source: CompatibleChangeSource | undefined) => void;
  beforeChange?: (changes: Array<CompatibleCellChange> | null, source: CompatibleChangeSource) => boolean | void;
  afterValidation?: (
    isValid: boolean,
    value: ReactNode,
    row: number,
    prop: string | number,
    source: CompatibleChangeSource
  ) => void;
  setInvalid?: (invalid: boolean) => void;
  maxRows?: number;
  testPrefix?: string;
  addCol?: boolean;
  metadata?: Array<MetadataField>;
  setMetadata?: (newMetadata: Array<MetadataField>) => void;
  metadataToColumnSettingMapper?: (metadata: MetadataField) => {
    columnHeader: string;
    columnSettings: Record<string, unknown>;
  };
  patchMetadataAfterSettingsUpdated?: () => void;
  buttonClasses?: string;
  overlay?: ReactNode;
  modalView?: boolean;
  addRow?: { title: string; tooltip?: string };
  afterAddRow?: () => void;
  defaultNewRowParams?: Array<{ col: number; value: string }>;
  autoAddRow?: boolean;
  greedyValidateRow?: boolean;
  enableRowHeaderControls?: boolean;
  afterContextMenuHide?: () => void;
  versionLabel?: string;
  cells?: Handsontable.GridSettings['cells'];
  customAfterRenderer?: (
    td: HTMLTableCellElement,
    row: number,
    col: number,
    prop: string | number,
    value: unknown,
    cellProperties: object
  ) => void;
  fillHandle?: boolean | Handsontable.GridSettings['fillHandle'];
}

const SpreadsheetV16: FC<SpreadsheetV16Props> = ({
  data,
  settings,
  setSettings,
  innerRef,
  className = '',
  afterChange,
  afterCreateRow,
  afterRemoveRow,
  beforeChange,
  afterValidation,
  setInvalid,
  maxRows = Infinity,
  testPrefix = 'spreadsheet-v16',
  // Metadata functionality
  addCol = true,
  metadata,
  setMetadata,
  metadataToColumnSettingMapper,
  patchMetadataAfterSettingsUpdated,
  buttonClasses,
  overlay,
  modalView = true,
  // Add row functionality
  addRow,
  afterAddRow,
  defaultNewRowParams,
  autoAddRow = false,
  greedyValidateRow,
  // Row header controls
  enableRowHeaderControls = false,
  afterContextMenuHide = () => {},
  // Cell-level configuration
  cells: cellsProp,
  customAfterRenderer,
  fillHandle,
}) => {
  const cells = cellsProp ?? settings.cells;

  const [cellErrors, setCellErrors] = useState<Record<string, SpreadSheetErrorV16>>({});
  const cellErrorsRef = useRef<Record<string, SpreadSheetErrorV16>>({});

  const [open, setOpen] = useState<boolean>(false);

  const customValidators = useMemo<Record<string, CustomValidationV16>>(() => {
    if (_notNil(settings.columns) && Array.isArray(settings.columns)) {
      return buildCustomValidatorsV16(settings.columns as Array<CustomColumnSettingsV16>);
    }
    return {};
  }, [settings.columns]);

  const customValidatorsRef = useRef<Record<string, CustomValidationV16>>(customValidators);
  customValidatorsRef.current = customValidators;

  useEffect(() => {
    setInvalid?.(_isNotEmpty(cellErrors));
  }, [cellErrors, setInvalid]);

  useEffect(() => {
    const hot = innerRef?.current?.hotInstance;
    if (isCompatibleHotInstance(hot)) {
      validateAllCells(hot);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.columns, data]);

  useEffect(() => {
    const hot = innerRef?.current?.hotInstance;
    if (_notNil(hot) && _notNil(settings.columns) && isCompatibleHotInstance(hot)) {
      if (Array.isArray(settings.columns)) {
        hot.updateSettings({
          columns: settings.columns,
        });
        hot.render();
      }
    }
  }, [settings.columns, innerRef]);

  const validateAllCells = (hot: CompatibleHotInstance): void => {
    setTimeout(() => {
      if (hot?.isDestroyed ?? true) {
        return;
      }
      if (_notNil(settings.minSpareRows) && settings.minSpareRows > 0) {
        const rowCount = hot.countRows();
        const rowsToValidate = Math.max(0, rowCount - settings.minSpareRows);
        hot.validateRows([...Array(rowsToValidate).keys()]);
      } else {
        hot.validateCells();
      }
    }, 0);
  };

  const addNewRow = (): void => {
    const hot = innerRef?.current?.hotInstance;

    if (_notNil(hot) && isCompatibleHotInstance(hot)) {
      if (_isNotEmpty(defaultNewRowParams)) {
        const row = hot.countRows();
        const changes: Array<[number, number, CellValue]> = defaultNewRowParams.map((p) => [row, p.col, p.value]);
        hot.setDataAtCell(changes, 'insert_row');
      } else {
        hot.alter('insert_row_below', hot.countRows() - 1, 1);
      }
    }
  };

  const handleAfterCreateRow = (index: number, amount: number, source?: CompatibleChangeSource): void => {
    const hot = innerRef?.current?.hotInstance;
    if (source === 'auto' && _notNil(hot) && autoAddRow && isCompatibleHotInstance(hot)) {
      hot.selectCell(index, 0);
    }
    afterCreateRow?.(index, amount, source);
  };

  const handleAfterUndoRedo = useCallback((): void => {
    const hot = innerRef?.current?.hotInstance;
    if (isCompatibleHotInstance(hot)) {
      setTimeout(() => {
        validateAllCells(hot);
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [innerRef, settings.minSpareRows, data.length]);

  const isRowEmpty = (hot: CompatibleHotInstance, row: number, colCount: number): boolean => {
    for (let col = 0; col < colCount; col++) {
      const cellValue = hot.getDataAtCell(row, col);
      if (_notNil(cellValue) && cellValue !== '') {
        return false;
      }
    }
    return true;
  };

  const handleAfterOnCellCornerDblClick = (): void => {
    const hot = innerRef?.current?.hotInstance;

    if (_notNil(hot) && isCompatibleHotInstance(hot)) {
      const selection = hot.getSelected();
      if (_notNil(selection) && Array.isArray(selection) && selection.length > 0) {
        const [r1, c1, r2, c2] = selection[0];
        const startRow = Math.min(r1, r2);
        const endRow = Math.max(r1, r2);
        const startCol = Math.min(c1, c2);
        const endCol = Math.max(c1, c2);

        const sourceData: Array<Array<unknown>> = [];
        for (let row = startRow; row <= endRow; row++) {
          const rowData: Array<unknown> = [];
          for (let col = startCol; col <= endCol; col++) {
            rowData.push(hot.getDataAtCell(row, col));
          }
          sourceData.push(rowData);
        }

        const allSourceEmpty = sourceData.every((row) => row.every((val) => !_notNil(val) || val === ''));
        if (allSourceEmpty) {
          return;
        }

        const totalRows = hot.countRows();
        const colCount = hot.countCols();
        const minSpare = settings.minSpareRows ?? 0;

        let lastFillableRow = totalRows - 1;
        if (minSpare > 0) {
          lastFillableRow = totalRows - 1 - minSpare;
        } else {
          while (lastFillableRow > endRow && isRowEmpty(hot, lastFillableRow, colCount)) {
            lastFillableRow--;
          }
        }

        const sourceHeight = endRow - startRow + 1;
        const rowsToFill = lastFillableRow - endRow;

        if (rowsToFill > 0 && sourceHeight > 0) {
          const repeatCount = Math.ceil(rowsToFill / sourceHeight);

          const fillData: Array<Array<unknown>> = [];
          for (let i = 0; i < repeatCount; i++) {
            sourceData.forEach((row) => {
              fillData.push([...row]);
            });
          }

          const finalFillData = fillData.slice(0, rowsToFill);

          const changes: Array<[number, number, CellValue]> = [];
          for (let rowOffset = 0; rowOffset < finalFillData.length; rowOffset++) {
            const targetRow = endRow + 1 + rowOffset;
            if (targetRow > lastFillableRow) {
              break;
            }

            const fillRow = finalFillData[rowOffset];
            for (let colOffset = 0; colOffset < fillRow.length; colOffset++) {
              const targetCol = startCol + colOffset;
              if (targetCol > endCol) {
                break;
              }

              changes.push([targetRow, targetCol, fillRow[colOffset] as CellValue]);
            }
          }

          if (_isNotEmpty(changes)) {
            hot.setDataAtCell(changes);
          }
        }
      }
    }
  };

  const handleAfterRemoveRow = (
    index: number,
    amount: number,
    physicalRows: Array<number>,
    source: CompatibleChangeSource | undefined
  ): void => {
    const hot = innerRef?.current?.hotInstance;
    if (isCompatibleHotInstance(hot)) {
      const totalRows = hot.countRows();
      const deletedRowNumbers = new Set(physicalRows);

      const updatedErrors = { ...cellErrorsRef.current };

      Object.keys(updatedErrors).forEach((errorKey) => {
        const error = updatedErrors[errorKey];
        if (deletedRowNumbers.has(error.rowNumber)) {
          delete updatedErrors[errorKey];
        }
      });

      const spareRows = settings.minSpareRows ?? 0;
      const entriesToRemove = Object.entries(updatedErrors).filter(
        ([, item]) => spareRows > 0 && item.rowNumber >= totalRows - spareRows
      );
      entriesToRemove.forEach(([keyToRemove]) => {
        delete updatedErrors[keyToRemove];
      });

      cellErrorsRef.current = updatedErrors;
      setCellErrors(updatedErrors);

      validateAllCells(hot);
    }

    afterRemoveRow?.(source);
  };

  const handleAfterChange = (changes: Array<CompatibleCellChange> | null, source: CompatibleChangeSource): void => {
    const hot = innerRef?.current?.hotInstance;
    if (source === 'insert_row' && _notNil(hot) && isCompatibleHotInstance(hot)) {
      afterAddRow?.();
      hot.validateRows((changes ?? []).map((change) => change[0]));
    }

    changes?.forEach((change) => {
      if (_notNil(hot) && isCompatibleHotInstance(hot)) {
        if (_notNil(greedyValidateRow) && greedyValidateRow) {
          hot.validateRows([change[0]]);
        }

        const columnIdentifier = change[1];
        const normalizedProp =
          typeof columnIdentifier === 'string' ? columnIdentifier : hot.colToProp(columnIdentifier);

        if (_notNil(normalizedProp)) {
          const normalizedKey = String(normalizedProp);
          const linkedColumns = customValidators?.[normalizedKey]?.linkedValidationColumns;

          if (_notNil(linkedColumns) && linkedColumns.length > 0) {
            hot.validateColumns(
              linkedColumns.map((linkedColumn) => {
                const colIndex = Number(hot.propToCol(linkedColumn));
                if (colIndex >= 0) {
                  return colIndex;
                }
                const numericProp = Number(linkedColumn);
                return !isNaN(numericProp) ? Number(hot.propToCol(numericProp)) : colIndex;
              })
            );
          }
        }
      }
    });

    afterChange?.(changes, source);
  };

  const addNewColumns = useCallback(
    (metadataIdsToAdd: Array<string | ID> = []): void => {
      const filteredMetadata = metadata?.filter(({ id }) => metadataIdsToAdd.includes(id)) ?? [];
      if (!_notNil(settings) || filteredMetadata.length === 0) {
        return;
      }

      const updatedSettings = filteredMetadata.reduce(
        ({ colHeaders, columns }, mdToAdd) => {
          const { columnHeader, columnSettings } = (
            metadataToColumnSettingMapper ?? defaultMetadataToColumnSettingMapperV16
          )(mdToAdd);
          return {
            colHeaders: [...colHeaders, columnHeader],
            columns: [...columns, columnSettings],
          };
        },
        { colHeaders: [] as Array<string>, columns: [] as Array<ColumnSettings> }
      );

      if (!Array.isArray(settings.columns)) {
        settings.columns = [...updatedSettings.columns];
      } else {
        settings.columns = [...settings.columns, ...updatedSettings.columns];
      }

      if (!Array.isArray(settings.colHeaders)) {
        settings.colHeaders = [...updatedSettings.colHeaders];
      } else {
        settings.colHeaders = [...(settings.colHeaders as Array<string>), ...updatedSettings.colHeaders];
      }

      const unusedMetadata = (metadata ?? []).filter(({ id: metadataId }) =>
        Array.isArray(settings.columns)
          ? !settings.columns.some(({ metaID }: ColumnSettings & { metaID?: string | number }) => metaID === metadataId)
          : true
      );

      setMetadata?.(unusedMetadata);

      if (_notNil(patchMetadataAfterSettingsUpdated)) {
        patchMetadataAfterSettingsUpdated();
      }

      if (_notNil(setSettings)) {
        setSettings(settings);
      }
    },
    [settings, metadata, setMetadata, patchMetadataAfterSettingsUpdated, setSettings, metadataToColumnSettingMapper]
  );

  const resolveColumnIndex = useCallback((hotInstance: CompatibleHotInstance, columnProp: unknown): number => {
    if (typeof columnProp === 'number') {
      return columnProp;
    }
    if (typeof columnProp === 'string') {
      return hotInstance.propToCol(columnProp);
    }

    const columns = hotInstance.getSettings().columns;
    if (Array.isArray(columns)) {
      for (let i = 0; i < columns.length; i += 1) {
        if ((columns[i] as ColumnSettings).data === columnProp) {
          return i;
        }
      }
    }

    return -1;
  }, []);

  const handleAfterRenderer = useCallback(
    (
      td: HTMLTableCellElement,
      row: number,
      col: number,
      prop: string | number,
      value: unknown,
      cellProperties: object
    ): void => {
      const errorKey = `${col},${row}`;
      const error = cellErrorsRef.current[errorKey];

      if (_notNil(error)) {
        const hot = innerRef?.current?.hotInstance;
        if (_notNil(hot) && isCompatibleHotInstance(hot)) {
          const cellMeta = hot.getCellMeta(row, col);
          const normalizedProp = String(prop);
          const customMessage = customValidatorsRef.current[normalizedProp]?.validationErrorMessage;
          const rowData = _notNil(customMessage) ? hot.getSourceDataAtRow(row) : undefined;
          const errorMessage = createValidationErrorV16(error, cellMeta.type, customMessage, rowData);
          td.setAttribute('data-tooltip-id', 'global-tooltip-id');
          td.setAttribute('data-tooltip-content', errorMessage);
          td.setAttribute('data-type', 'error');
        }
      } else {
        clearCellTooltip(td);
      }

      customAfterRenderer?.(td, row, col, prop, value, cellProperties);
    },
    [innerRef, customAfterRenderer]
  );

  const handleAfterValidate = (
    isValid: boolean,
    value: ReactNode,
    row: number,
    prop: unknown,
    source: unknown
  ): void => {
    const hot = innerRef?.current?.hotInstance;
    const normalizedProp = typeof prop === 'string' || typeof prop === 'number' ? prop : '';

    const notifyValidation = () => {
      afterValidation?.(isValid, value, row, normalizedProp as string | number, source as CompatibleChangeSource);
    };

    if (!_notNil(hot) || !isCompatibleHotInstance(hot)) {
      return;
    }

    const colIndex = resolveColumnIndex(hot, prop);
    const totalRows = hot.countRows();
    const minSpareRows = settings.minSpareRows ?? 0;
    const isSpareRow = minSpareRows > 0 && row >= totalRows - minSpareRows;

    if (colIndex === -1 || isSpareRow) {
      notifyValidation();
      return;
    }

    const errorKey = `${colIndex},${row}`;

    if (isValid) {
      delete cellErrorsRef.current[errorKey];
      setCellErrors((prev) => {
        const updated = { ...prev };
        delete updated[errorKey];
        return updated;
      });
    } else {
      const error = { value, rowNumber: row, column: String(normalizedProp).replace('meta.', '') };
      cellErrorsRef.current[errorKey] = error;
      setCellErrors((prev) => ({ ...prev, [errorKey]: error }));
    }

    notifyValidation();
  };

  const componentName = 'Spreadsheet';
  const hotClassName =
    Array.isArray(settings.className) && settings.className.length > 0
      ? settings.className.join(' ')
      : typeof settings.className === 'string'
        ? settings.className
        : undefined;

  return (
    <div
      className={`hotSpreadSheet ${className} spreadSheetV16`}
      {...({ 'data-test-component': componentName, 'data-test-element': 'container' } as Record<string, string>)}
      data-testid={`${testPrefix}__hot`}
    >
      <span className={`${buttonClasses ?? ''}`} style={{ flexDirection: 'row' }}>
        <span className="ml-auto">
          {addCol && (
            <Button
              onClick={() => setOpen(!open)}
              className="mb3 self-end br-pill"
              icon="add_new"
              paleBlue
              disabled={_isNotEmpty(overlay) || _isEmpty(metadata)}
              tooltip={_isEmpty(metadata) ? 'No metadata available' : undefined}
              testId={`${testPrefix}__add-metadata`}
            >
              Add metadata
            </Button>
          )}
        </span>
      </span>
      {open && (
        <div className="flex justify-center pb2">
          <ReactModal
            isOpen={open}
            className="ui-modal"
            style={{
              overlay: { backgroundColor: 'rgba(00,00,00,.5)' },
              content: { maxHeight: '100vh' },
            }}
            ariaHideApp={false}
          >
            <AddMetaColumn
              metadata={metadata}
              callback={(columns: Array<string> | null) => {
                if (_notNil(columns)) {
                  addNewColumns(columns);
                }
                setOpen(false);
              }}
            />
          </ReactModal>
        </div>
      )}
      <div
        className={`${modalView ? 'ui-card' : 'ui-card-hot'} spreedSheetV16`}
        style={{ display: _isNotEmpty(overlay) ? 'none' : 'block', position: 'relative' }}
        data-test-element="hot-container"
        data-testid={`${testPrefix}__hot-table`}
      >
        <span className="hot-version-label">v16</span>
        <HotTable
          data={data}
          licenseKey={AppConfig.handsOnTableLicence}
          maxRows={maxRows}
          ref={innerRef}
          beforeChange={beforeChange as unknown as Handsontable.GridSettings['beforeChange']}
          afterRemoveRow={handleAfterRemoveRow as unknown as Handsontable.GridSettings['afterRemoveRow']}
          afterValidate={handleAfterValidate as unknown as Handsontable.GridSettings['afterValidate']}
          afterRenderer={handleAfterRenderer as unknown as Handsontable.GridSettings['afterRenderer']}
          afterContextMenuHide={afterContextMenuHide}
          afterGetRowHeader={
            enableRowHeaderControls && settings.rowHeaders === true ? rowHeaderWithControlsRendererV16 : undefined
          }
          colHeaders={settings.colHeaders}
          headerClassName="custom-header"
          rowHeaders={settings.rowHeaders}
          height={settings.height}
          rowHeights={settings.rowHeights}
          manualColumnResize={settings.manualColumnResize}
          outsideClickDeselects={settings.outsideClickDeselects}
          hiddenColumns={settings.hiddenColumns}
          className={hotClassName}
          columns={settings.columns}
          columnSorting={settings.columnSorting}
          contextMenu={settings.contextMenu}
          minSpareRows={Number(settings.minSpareRows ?? 0)}
          stretchH={settings.stretchH}
          colWidths={settings.colWidths}
          fixedColumnsStart={settings.fixedColumnsStart}
          afterChange={handleAfterChange as unknown as Handsontable.GridSettings['afterChange']}
          afterCreateRow={handleAfterCreateRow as unknown as Handsontable.GridSettings['afterCreateRow']}
          afterOnCellCornerDblClick={handleAfterOnCellCornerDblClick}
          afterUndo={handleAfterUndoRedo}
          afterRedo={handleAfterUndoRedo}
          fillHandle={fillHandle ?? { autoInsertRow: false }}
          cells={cells}
          testId={`${testPrefix}__hot-table-inner`}
        />
        {_isNotEmpty(addRow) && (
          <div className="pa3 bt b--moon-gray">
            <Button
              onClick={addNewRow}
              className="br-pill"
              icon="add_new"
              paleBlue
              disabled={_isNotEmpty(addRow.tooltip) || _isNotEmpty(overlay)}
              tooltip={addRow.tooltip}
              testId={`${testPrefix}__add-row`}
            >
              <span>{addRow.title}</span>
            </Button>
          </div>
        )}
      </div>
      {_isNotEmpty(overlay) && (
        <div className={'tableHolder'}>
          <div className="overlay">{overlay}</div>
        </div>
      )}
    </div>
  );
};

export default SpreadsheetV16;
